import { useEffect, useRef, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CloseIcon, GripIcon, PlusIcon } from 'components/icons'
import {
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form'
import { FormSetValue } from 'types/input'

type Props<T extends FieldValues> = {
  label?: string
  placeholder?: string
  emptyText?: string
  hintText?: string
  defaultValues?: string[]
  errors?: FieldErrors<T>
  loading?: boolean
} & FormSetValue<T>

type RowProps = {
  id: string
  label: string
  position: number
  onDelete: () => void
  disabled?: boolean
}

const Row = ({ id, label, position, onDelete, disabled }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2.5 px-1 py-2 ${
        isDragging ? 'z-10 rounded-md bg-gray-50 shadow-sm' : ''
      }`}
    >
      <button
        type="button"
        aria-label={`Drag ${label}`}
        disabled={disabled}
        {...attributes}
        {...listeners}
        className="flex size-7 shrink-0 touch-none items-center justify-center rounded text-gray-300 outline-none transition-colors hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing disabled:opacity-30 [&>svg]:size-5"
      >
        <GripIcon />
      </button>

      <span
        className={`flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold tabular-nums ring-1 ${
          isDragging
            ? 'bg-primary text-white ring-primary'
            : 'bg-white text-gray-400 ring-gray-200 group-hover:ring-gray-300'
        }`}
      >
        {position}
      </span>

      <span className="flex-1 truncate py-0.5 text-sm text-gray-700">
        {label}
      </span>

      <button
        type="button"
        aria-label={`Remove ${label}`}
        disabled={disabled}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onDelete}
        className="flex size-5 shrink-0 items-center justify-center rounded text-gray-300 opacity-0 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-0 group-hover:opacity-100 [&>svg]:size-2.5"
      >
        <CloseIcon />
      </button>
    </div>
  )
}

/**
 * Reusable add / remove / drag-to-reorder list for a string[] form field.
 * Element order in the array IS the persisted order - there is no separate
 * order field. Add and delete behave like DynamicInputList; dragging commits
 * the new order into the same field via setValue.
 */
const SortableList = <T extends FieldValues>({
  setValue,
  name,
  label,
  placeholder = 'Add an item',
  emptyText = 'No items yet',
  hintText,
  defaultValues,
  errors,
  loading = false,
}: Props<T>) => {
  const [items, setItems] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')

  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (defaultValues) setItems(defaultValues)
  }, [defaultValues])

  const commit = (value: string[]) => {
    setItems(value)
    ;(setValue as UseFormSetValue<T>)(
      name,
      value as PathValue<T, string & Path<T>>,
      { shouldValidate: true, shouldDirty: true },
    )
  }

  const addItem = () => {
    const input = draft.trim()
    if (!input) return

    if (items.some((item) => item.toLowerCase() === input.toLowerCase())) {
      setError('Already added')
      return
    }

    commit([...items, input])
    setDraft('')
    setError('')
    inputRef.current?.focus()
  }

  const deleteItem = (index: number) => {
    const next = [...items]
    next.splice(index, 1)
    commit(next)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = items.indexOf(String(active.id))
    const newIndex = items.indexOf(String(over.id))
    if (oldIndex === -1 || newIndex === -1) return

    commit(arrayMove(items, oldIndex, newIndex))
  }

  return (
    <div>
      {(label || items.length > 1) && (
        <div className="mb-3 flex items-baseline justify-between">
          {label && (
            <p className="text-xs font-semibold text-sidebar-primary">
              {label}
            </p>
          )}
          {items.length > 1 && (
            <p className="text-[11px] text-gray-400">Drag to reorder</p>
          )}
        </div>
      )}

      {hintText && <p className="mb-2 text-[11px] text-gray-500">{hintText}</p>}

      <div className="rounded-md border border-gray-200">
        {items.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              <div className="px-2 py-1">
                {items.map((item, index) => (
                  <Row
                    key={item}
                    id={item}
                    label={item}
                    position={index + 1}
                    onDelete={() => deleteItem(index)}
                    disabled={loading}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="px-4 py-5 text-center text-sm text-gray-400">
            {emptyText}
          </p>
        )}

        <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2">
          <input
            ref={inputRef}
            value={draft}
            disabled={loading}
            onChange={(e) => {
              setDraft(e.target.value)
              setError('')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addItem()
              }
            }}
            placeholder={placeholder}
            className="w-full bg-transparent py-1 text-sm text-gray-800 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            disabled={loading || !draft.trim()}
            onClick={addItem}
            aria-label="Add item"
            className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-gray-100 disabled:hover:text-gray-500 [&>svg]:size-2.5"
          >
            <PlusIcon />
          </button>
        </div>
      </div>

      <p className="mt-2 text-xs text-red-600">
        {error || (errors?.[name]?.message as string) || ''}
      </p>
    </div>
  )
}

export default SortableList
